/**
 * Profile Service
 *
 * Handles user profile-related operations including user data management,
 * preferences, trial settings, and account information.
 */

import { SERVICE_NAMES } from '../core/constants';
import {
  InternalSDKOperations,
  LanguageCode,
  OnboardingChecklist,
  UpdateOnboardingChecklistPayload,
  UserIntent,
} from '../types';
import { BaseService } from './base-service';
import { NetworkDetectionManager } from '../utils/network-detection-manager';

/**
 * Service class for managing user profile data and settings
 */
export class ProfileService extends BaseService {
  protected serviceName = SERVICE_NAMES.PROFILE;
  /**
   * Creates a new ProfileService instance
   * @param internalOps - Internal SDK operations interface
   * @param networkManager - Optional shared network detection manager
   */
  constructor(
    internalOps: InternalSDKOperations,
    networkManager?: NetworkDetectionManager,
  ) {
    super(internalOps, networkManager);
  }

  async setUserReferrerID(
    referrerId: string,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'setUserReferrerID';
    const parameters = { referrerId };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateClickID(clickID: string, version: string = 'v1'): Promise<void> {
    const functionName = 'updateClickID';
    const parameters = { clickID };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateName(name: string, version: string = 'v1'): Promise<void> {
    const functionName = 'updateName';
    const parameters = { name };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updatePhotoURL(
    photoURL: string,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updatePhotoURL';
    const parameters = { photoURL };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateTargetLanguage(
    targetLanguage: LanguageCode,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateTargetLanguage';
    const parameters = { targetLanguage };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateNativeLanguageDB(
    nativeLanguage: LanguageCode,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateNativeLanguageDB';
    const parameters = { nativeLanguage };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateTrial(
    trialEndDate: string, // ISO 8601 date-time string, example: "2025-08-28T13:23:06.000Z"
    trialPeriod: number,
    trialStartDate: string, // ISO 8601 date-time string, example: "2025-08-28T13:23:06.000Z"
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateTrial';
    const parameters = { trialEndDate, trialPeriod, trialStartDate };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUUID(uuid: string, version: string = 'v1'): Promise<void> {
    const functionName = 'updateUUID';
    const parameters = { uuid };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateIsUserTrialEligible(
    isUserTrialEligible: boolean,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateIsUserTrialEligible';
    const parameters = { isUserTrialEligible };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUsedLanguagesIds(
    uniqueUsedLanguagesIds: string[],
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateUsedLanguagesIds';
    const parameters = { uniqueUsedLanguagesIds };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserDefaultProperties(
    user: {
      displayName?: string;
      email: string;
      photoURL?: string;
      uid: string;
    },
    targetLanguage: LanguageCode,
    referrer: string,
    dateLastPlayed: number,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserDefaultProperties';
    const parameters = { user, targetLanguage, referrer, dateLastPlayed };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async updateUserInfo(
    userInfo: Record<string, unknown>,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateUserInfo';
    const parameters = { userInfo };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  /**
   * Writes the onboarding intent to the user record via PATCH /v1/users/me.
   * Re-sending the same intent is a harmless merge, so there is no
   * idempotency key. Deliberately NOT wrapped in offline support: the offline
   * queue replays operations as POST, and the caller keeps its own
   * "synced" flag so a failed send is simply retried on the next app open.
   */
  async updateUserIntent(
    intent: UserIntent,
    version: string = 'v1',
  ): Promise<void> {
    await this.internalOps.makeRequest<{}>(
      SERVICE_NAMES.PROFILE,
      'users/me',
      { intent },
      version,
      { method: 'PATCH' },
    );
  }

  async setOrUpdateUserDataOnSignUp(
    email: string,
    name: string,
    targetLanguage: LanguageCode,
    nativeLanguage: LanguageCode,
    photoURL?: string,
    uuid?: string,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'setOrUpdateUserDataOnSignUp';
    const parameters = {
      email,
      name,
      photoURL,
      targetLanguage,
      nativeLanguage,
      uuid,
    };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.PROFILE,
        functionName,
        parameters,
        version,
      ),
    );
  }

  async getUserProfile(
    version: string = 'v1',
  ): Promise<{ profile: Record<string, unknown> }> {
    const response = await this.internalOps.makeRequest<{
      profile: Record<string, unknown>;
    }>(SERVICE_NAMES.PROFILE, 'getUserProfile', {}, version);

    return response.data;
  }

  async getUserData(version: string = 'v1'): Promise<{
    profile: Record<string, unknown>;
    settings: Record<string, unknown>;
    reminders: Record<string, unknown>;
  }> {
    const response = await this.internalOps.makeRequest<{
      profile: Record<string, unknown>;
      settings: Record<string, unknown>;
      reminders: Record<string, unknown>;
    }>(SERVICE_NAMES.PROFILE, 'getUserData', {}, version);

    return response.data;
  }

  async getOnboardingChecklist(version: string = 'v1'): Promise<{
    onboardingChecklist: OnboardingChecklist;
    createdAt: number;
    isRewardClaimed: boolean;
  }> {
    const response = await this.internalOps.makeRequest<{
      onboardingChecklist: OnboardingChecklist;
      createdAt: number;
      isRewardClaimed: boolean;
    }>(SERVICE_NAMES.PROFILE, 'getOnboardingChecklist', undefined, version, {
      method: 'GET',
    });
    return response.data;
  }

  async updateOnboardingChecklist(
    checklist: UpdateOnboardingChecklistPayload,
    version: string = 'v1',
  ): Promise<void> {
    const functionName = 'updateOnboardingChecklist';

    await this.executeWithOfflineSupport(
      functionName,
      checklist as Record<string, unknown>,
      () =>
        this.internalOps.makeRequest<{}>(
          SERVICE_NAMES.PROFILE,
          functionName,
          checklist as Record<string, unknown>,
          version,
        ),
    );
  }
}
